import { Avatar, AvatarFallback, AvatarImage } from '@up2stay/up2promo-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import {
  Bell,
  ChevronsUpDown,
  Clapperboard,
  HelpCircle,
  ListChecks,
  LogOut,
  Megaphone,
  Phone,
  User,
  UserRoundSearch,
} from 'lucide-react';

import { LogoutConfirmDialog } from '@/core/components/logout-confirm-dialog';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import { useIntercom } from '@/core/hooks/use-intercom';
import { shouldMountTourKitPro } from '@/core/guidance/tour-kit-license';
import { useIsPlatformAdmin } from '@/core/hooks/use-is-platform-admin';
import { useLogout } from '@/core/hooks/use-logout';
import { useNotificationHubBadgeCount } from '@/core/hooks/use-notifications';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-routes';
import { useAuthStore } from '@/core/store/auth.store';

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from './sidebar';
import { switcherAvatarFallbackClass } from './switcher-avatar-styles';
import { ThemeSwitcher } from './theme-switcher';
import type { SidebarUser } from './types';

type NavUserProps = {
  user: SidebarUser;
};

export function NavUser({ user }: NavUserProps) {
  const dict = useDictionary();
  const { isMobile } = useSidebar();
  const { navigateFromSidebar, navigateShell } = useNavigateShell();
  const { dialogOpen, setDialogOpen, requestLogout, confirmLogout } =
    useLogout();
  const { badgeCount } = useNotificationHubBadgeCount();
  const intercom = useIntercom();
  const isPlatformAdmin = useIsPlatformAdmin();
  const isImpersonating = useAuthStore((state) =>
    Boolean(state.user?.impersonation),
  );
  // Platform RoleEnum.admin (up2stay staff), not workspace Administrator.
  const showPlatformAdminTools =
    !isMobile && isPlatformAdmin && !isImpersonating;
  const showTourKitProTools = shouldMountTourKitPro();
  const phoneVerified = useAuthStore((state) => state.user?.phoneVerified);

  // Get initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="rounded-[11px] border border-sidebar-border bg-[color:var(--surface-2)] data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="relative">
                <Avatar
                  key={`nav-user-${user.name}-${user.avatar || 'no-avatar'}`}
                  className="h-8 w-8 rounded-lg"
                >
                  {user.avatar ? (
                    <AvatarImage src={user.avatar} alt={user.name} />
                  ) : null}
                  <AvatarFallback
                    className={`rounded-lg font-semibold ${switcherAvatarFallbackClass}`}
                  >
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                {badgeCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
                )}
              </div>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ms-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-start text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  {user.avatar && (
                    <AvatarImage src={user.avatar} alt={user.name} />
                  )}
                  <AvatarFallback
                    className={`rounded-lg font-semibold ${switcherAvatarFallbackClass}`}
                  >
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-start text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                asChild
                onSelect={(event) => event.preventDefault()}
                className="cursor-default focus:bg-transparent focus:text-foreground"
              >
                <div>
                  <ThemeSwitcher />
                </div>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => navigateFromSidebar(shellPaths.moreUserSettings)}
              >
                <User />
                {dict.nav.account}
              </DropdownMenuItem>
              {phoneVerified !== true ? (
                <DropdownMenuItem
                  onClick={() =>
                    navigateShell(shellPaths.moreUserSettingsPhone)
                  }
                >
                  <Phone />
                  {dict.nav.addPhoneForSms}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() =>
                  navigateFromSidebar(shellPaths.moreUserNotifications)
                }
                className="relative"
              >
                <Bell />
                {dict.nav.notifications}
                {badgeCount > 0 && (
                  <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-primary-solid text-xs font-medium text-primary-solid-foreground">
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  useGuidanceStore.getState().requestOrientationReplay();
                }}
              >
                <Clapperboard />
                {dict.guidance.tours.watchIntro}
              </DropdownMenuItem>
              {showTourKitProTools ? (
                <>
                  <DropdownMenuItem
                    onClick={() => navigateFromSidebar(shellPaths.moreWhatsNew)}
                  >
                    <Megaphone />
                    {dict.guidance.announcements.whatsNew}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      useGuidanceStore.getState().requestChecklistRestore();
                    }}
                  >
                    <ListChecks />
                    {dict.guidance.checklist.reopen}
                  </DropdownMenuItem>
                </>
              ) : null}
              <DropdownMenuItem
                onClick={() => {
                  void intercom.openSupport();
                }}
              >
                <HelpCircle />
                {dict.nav.helpSupport}
              </DropdownMenuItem>
              {showPlatformAdminTools ? (
                <DropdownMenuItem
                  onClick={() => navigateFromSidebar(shellPaths.moreSupport)}
                >
                  <UserRoundSearch />
                  {dict.nav.supportTools}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={requestLogout}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <LogOut />
              {dict.nav.signOut}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <LogoutConfirmDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onConfirm={confirmLogout}
        />
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
